import type { CSSProperties, FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import { cn } from '@/lib/utils'
import { useMotionPaused } from '@/lib/motion'
import { useInView } from '@/hooks/use-in-view'
import { AreaChart } from '@/components/charts/area-chart'
import { DaylightHeatmap } from '@/components/charts/daylight-heatmap'
import { RacingBars } from '@/components/charts/racing-bars'
import { Treemap } from '@/components/charts/treemap'
import { CountUp } from '@/components/generals/count-up'
import { FigLabel } from '@/components/generals/fig-label'
import { Reveal } from '@/components/generals/reveal'

import { STUDIO_FACTS } from '@/constants/studio'

interface EvidenceProps {
    projects: Array<ProjectSummary>
}

interface PanelProps {
    letter: string
    title: string
    note: string
    className?: string
    children: (run: boolean) => ReactNode
}

const CARBON_YEARS = ['2017', '2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025']
const CARBON_VALUES = [18, 24, 31, 38, 47, 55, 64, 72, 63]

const MATERIALS = [
    { name: 'Timber', share: 46 },
    { name: 'Concrete', share: 27 },
    { name: 'Steel', share: 14 },
    { name: 'Glass', share: 9 },
    { name: 'Other', share: 4 }
]

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

const Panel: FC<PanelProps> = ({ letter, title, note, className, children }): ReactNode => {
    const [ref, inView] = useInView<HTMLElement>({ rootMargin: '0px 0px -18% 0px' })

    return (
        <article
            ref={ref}
            data-reveal={inView ? 'in' : 'pre'}
            className={cn('flex min-w-0 flex-col border-b border-hairline px-gutter py-10 desk:px-12 desk:py-12', className)}
        >
            <header className='mb-8 flex items-start justify-between gap-6'>
                <div>
                    <p className='hud text-ink'>
                        {letter} / {title}
                    </p>

                    <p className='hud text-mute'>
                        {note}
                    </p>
                </div>

                <span className='draw-x mt-2 hidden h-px w-16 bg-hairline sm:block' aria-hidden='true' />
            </header>

            <div className='flex flex-1 flex-col justify-end'>
                {children(inView)}
            </div>
        </article>
    )
}

/**
 * Home section 08: four panels of portfolio data in one hairline grid.
 */
export const Evidence: FC<EvidenceProps> = ({ projects }): ReactNode => {
    const paused = useMotionPaused()

    return (
        <section id='evidence' aria-labelledby='evidence-title' className='relative border-t border-hairline'>
            <Reveal className='grid gap-8 px-gutter pb-14 pt-20 desk:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] desk:pb-20 desk:pt-28'>
                <div>
                    <FigLabel fig='08' title='Evidence' detail='Portfolio data' note={`${STUDIO_FACTS.projects} projects / ${STUDIO_FACTS.countries} countries / measured after handover`} />

                    <h2 id='evidence-title' className='display-lg mt-10'>
                        <span className='reveal-line'>
                            <span className='reveal-inner text-ink' style={delay(80)}>
                                What the buildings
                            </span>
                        </span>

                        <span className='reveal-line'>
                            <span className='reveal-inner text-dim' style={delay(170)}>
                                report back.
                            </span>
                        </span>
                    </h2>
                </div>

                <p className='reveal-fade body-copy self-end text-ink/85 desk:justify-self-end' style={delay(260)}>
                    Every figure below comes from readings taken in finished buildings, not from design-stage models. Hover or use the arrow keys on any chart to read exact values.
                </p>
            </Reveal>

            <div className='grid border-t border-hairline desk:grid-cols-2'>
                <Panel letter='A' title='Carbon avoided' note='t CO2e per year against a code-minimum baseline' className='desk:border-r'>
                    {run => (
                        <>
                            <div className='mb-8 flex flex-wrap items-end gap-x-4 gap-y-1'>
                                <p className='font-heading text-[clamp(56px,7vw,104px)] font-medium leading-[0.9] tracking-[-0.04em] text-ink'>
                                    <CountUp value={STUDIO_FACTS.carbonAvoided} decimals={0} suffix='' epoch={run && !paused ? 1 : 0} duration={1600} />
                                </p>

                                <p className='hud pb-2 text-ink'>
                                    t CO2e
                                    <span className='block text-mute'>
                                        Avoided 2017 to 2025
                                    </span>
                                </p>
                            </div>

                            <AreaChart
                                title='Carbon avoided per year, tonnes of CO2 equivalent, 2017 to 2025'
                                labels={CARBON_YEARS}
                                values={CARBON_VALUES}
                                unit='t'
                                seriesName='Avoided'
                            />
                        </>
                    )}
                </Panel>

                <Panel letter='B' title='Daylight' note='Share of each hour above 300 lux / 24 h by 12 months'>
                    {() => <DaylightHeatmap title='Daylight across a year of readings, hour by month' />}
                </Panel>

                <Panel letter='C' title='Performance' note={`${projects.length} monitored buildings, ranked`} className='desk:border-r'>
                    {run => <RacingBars projects={projects} run={run} />}
                </Panel>

                <Panel letter='D' title='Materials' note='Share of structural mass across the portfolio'>
                    {run => <Treemap title='Share of structural mass by material across the portfolio' items={MATERIALS} run={run} aspect={1.6} />}
                </Panel>
            </div>
        </section>
    )
}
